import { useMutation } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { post } from "api";
import { QRScanner } from "organisms/qr-scanner/QRScanner";
import { queryClient } from "pages/Root";

const redeem = debounce(
  ({
    scannedCode,
    packageId,
    isFromMembership,
    productId,
    locationId,
    quantity,
  }) =>
    post("/platform-user-package-items/redeem", {
      scannedCode,
      packageId,
      isFromMembership: isFromMembership === "true",
      productId,
      locationId,
      quantity,
    }),
  500,
  { leading: true, trailing: false }
);

const ScanRedeemTreatmentPage = () => {
  const [scannedQRCode, setScannedQRCode] = useState("");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();
  const [errorMessage, setErrorMessage] = useState(
    "Something went wrong - please contact support if this issue persists"
  );
  const isPackageActive = !(Number(state.remainingAmount) === 0);
  const mutation = useMutation({
    mutationFn: redeem,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          "/platform-user-packages/{packageId}",
          { packageId: state.packageId, locationId: state.locationId },
        ],
      });

      setScannedQRCode("");
      navigate(
        `/wallet/treatments/${state.packageId}?isFromMembership=${state.isFromMembership}&active=${isPackageActive}&comingFromScan=true&locationId=${state.locationId}`
      );
    },
    onError: (err: any) => {
      if (err.response.status === 400) {
        setErrorMessage("Unrecognized QR code - please try again");
      }
    },
  });

  const handleShowErrorMessage = useCallback(() => {
    if (mutation.isError) {
      setShowErrorMessage(true);
      return setTimeout(() => {
        setShowErrorMessage(false);
        setScannedQRCode("");
      }, 2000);
    }
  }, [mutation.isError]);

  useEffect(() => {
    const timeoutId = handleShowErrorMessage();
    return () => clearTimeout(timeoutId);
  }, [handleShowErrorMessage]);

  const handleScanResult = useCallback(() => {
    if (
      scannedQRCode &&
      !showErrorMessage &&
      !mutation.isLoading &&
      !mutation.isSuccess
    ) {
      mutation.mutate({
        scannedCode: scannedQRCode,
        packageId: state.packageId,
        locationId: state.locationId,
        isFromMembership: state.isFromMembership,
        productId: state.productId,
        quantity: state.quantity,
      });
    }
  }, [
    scannedQRCode,
    showErrorMessage,
    mutation,
    state.packageId,
    state.locationId,
    state.isFromMembership,
    state.productId,
    state.quantity,
  ]);

  useEffect(() => {
    handleScanResult();
  }, [handleScanResult]);

  return (
    <QRScanner
      errorMessage={showErrorMessage ? errorMessage : undefined}
      onClose={() => navigate(-1)}
      onResult={setScannedQRCode}
    />
  );
};

export default ScanRedeemTreatmentPage;
